/**
 * VÉRAULT TRAVELS OS - Settings Page
 * System settings including calendar integration, email, and API connections
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Settings, Calendar, Mail, Plane, Check, X, ExternalLink,
  Loader2, RefreshCw, Link2, AlertCircle, Unlink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== INTEGRATION STATUS CARD ====================

function IntegrationCard({ title, icon: Icon, status, onConnect, onDisconnect, children }) {
  const isConnected = status?.connected || status?.mode === 'live';
  
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 bg-[#F5F1EB]/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isConnected ? 'bg-[#8DA399]/10' : 'bg-[#E6E4DE]'
            }`}>
              <Icon className={`w-5 h-5 ${isConnected ? 'text-[#8DA399]' : 'text-[#8C8C8C]'}`} />
            </div>
            <div>
              <CardTitle className="text-base font-medium">{title}</CardTitle>
              <p className={`text-xs ${isConnected ? 'text-[#8DA399]' : 'text-[#8C8C8C]'}`}>
                {isConnected ? (
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3" /> Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Not connected
                  </span>
                )}
              </p>
            </div>
          </div>
          
          {isConnected ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDisconnect}
              className="text-[#8C8C8C] hover:text-[#B37E6B]"
            >
              <Unlink className="w-4 h-4 mr-1" />
              Disconnect
            </Button>
          ) : onConnect && (
            <Button
              variant="outline"
              size="sm"
              onClick={onConnect}
              className="border-[#C8A85A] text-[#C8A85A]"
            >
              <Link2 className="w-4 h-4 mr-1" />
              Connect
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {children}
      </CardContent>
    </Card>
  );
}

// ==================== CALENDAR SETTINGS ====================

function CalendarSettings({ status, onRefresh }) {
  const [connecting, setConnecting] = useState(false);
  
  const handleConnect = async () => {
    try {
      setConnecting(true);
      const res = await axios.post(`${API}/settings/calendar/connect`);
      
      if (res.data.auth_url) {
        // Open OAuth flow in new window
        window.open(res.data.auth_url, '_blank', 'width=600,height=700');
        toast.info('Complete the sign-in in the popup window');
        
        // Poll for connection status
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/settings/calendar`);
            if (statusRes.data.connected) {
              clearInterval(pollInterval);
              toast.success('Google Calendar connected!');
              onRefresh();
            }
          } catch {
            // Ignore polling errors
          }
        }, 2000);
        
        // Stop polling after 2 minutes
        setTimeout(() => clearInterval(pollInterval), 120000);
      } else {
        toast.error(res.data.message || 'Could not initiate connection');
      }
    } catch (err) {
      toast.error('Failed to connect calendar');
    } finally {
      setConnecting(false);
    }
  };
  
  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/settings/calendar/disconnect`);
      toast.success('Calendar disconnected');
      onRefresh();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };
  
  return (
    <IntegrationCard
      title="Google Calendar"
      icon={Calendar}
      status={status}
      onConnect={connecting ? null : handleConnect}
      onDisconnect={handleDisconnect}
    >
      <div className="space-y-4">
        {status?.configured ? (
          <>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[#8C8C8C] text-xs uppercase">Mode</p>
                <p className="text-[#1A1A1A]">{status.mode === 'live' ? 'Live Sync' : 'Mock Mode'}</p>
              </div>
              <div>
                <p className="text-[#8C8C8C] text-xs uppercase">Direction</p>
                <p className="text-[#1A1A1A]">VT → Calendar (One-way)</p>
              </div>
              {status.calendar_name && (
                <div>
                  <p className="text-[#8C8C8C] text-xs uppercase">Calendar</p>
                  <p className="text-[#1A1A1A]">{status.calendar_name}</p>
                </div>
              )}
              {status.user_email && (
                <div>
                  <p className="text-[#8C8C8C] text-xs uppercase">Account</p>
                  <p className="text-[#1A1A1A]">{status.user_email}</p>
                </div>
              )}
            </div>
            
            {status.mode !== 'live' && (
              <div className="bg-[#C8A85A]/10 rounded-lg p-3 text-sm text-[#C8A85A]">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Running in mock mode. Events are logged but not synced to Google Calendar.
              </div>
            )}
          </>
        ) : (
          <div className="bg-[#F5F1EB] rounded-lg p-4 text-sm text-[#595959]">
            <p className="mb-2">To enable Google Calendar sync:</p>
            <ol className="list-decimal list-inside space-y-1 text-[#8C8C8C]">
              <li>Create a Google Cloud project</li>
              <li>Enable Calendar API</li>
              <li>Create OAuth credentials</li>
              <li>Add credentials to environment</li>
            </ol>
          </div>
        )}
      </div>
    </IntegrationCard>
  );
}

// ==================== EMAIL SETTINGS ====================

function EmailSettings({ status }) {
  return (
    <IntegrationCard
      title="Email (Resend)"
      icon={Mail}
      status={status}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[#8C8C8C] text-xs uppercase">Provider</p>
            <p className="text-[#1A1A1A]">{status?.provider || 'Resend'}</p>
          </div>
          <div>
            <p className="text-[#8C8C8C] text-xs uppercase">Mode</p>
            <p className="text-[#1A1A1A]">{status?.mode === 'live' ? 'Live' : 'Stub (Logging)'}</p>
          </div>
          {status?.sender_email && (
            <div className="col-span-2">
              <p className="text-[#8C8C8C] text-xs uppercase">Sender</p>
              <p className="text-[#1A1A1A]">{status.sender_email}</p>
            </div>
          )}
        </div>
        
        {!status?.configured && (
          <div className="bg-[#F5F1EB] rounded-lg p-3 text-sm text-[#595959]">
            <p>Add RESEND_API_KEY to environment to enable email sending.</p>
          </div>
        )}
      </div>
    </IntegrationCard>
  );
}

// ==================== FLIGHT STATUS SETTINGS ====================

function FlightStatusSettings({ status }) {
  return (
    <IntegrationCard
      title="Flight Status API"
      icon={Plane}
      status={{ connected: status?.providers?.amadeus?.available }}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[#8C8C8C] text-xs uppercase">Active Provider</p>
            <p className="text-[#1A1A1A]">{status?.active_provider || 'stub'}</p>
          </div>
          <div>
            <p className="text-[#8C8C8C] text-xs uppercase">Live Data</p>
            <p className="text-[#1A1A1A]">{status?.providers?.amadeus?.is_live ? 'Yes' : 'No (Stored)'}</p>
          </div>
        </div>
        
        {/* Provider Status */}
        <div className="space-y-2">
          <p className="text-xs text-[#8C8C8C] uppercase">Providers</p>
          {status?.providers && Object.entries(status.providers).map(([name, prov]) => (
            <div key={name} className="flex items-center justify-between p-2 bg-[#F5F1EB] rounded">
              <span className="text-sm capitalize">{name}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${
                prov.available ? 'bg-[#8DA399]/10 text-[#8DA399]' : 'bg-[#E6E4DE] text-[#8C8C8C]'
              }`}>
                {prov.available ? 'Available' : prov.configured ? 'Configured' : 'Not Configured'}
              </span>
            </div>
          ))}
        </div>
        
        {!status?.providers?.amadeus?.configured && (
          <div className="bg-[#F5F1EB] rounded-lg p-3 text-sm text-[#595959]">
            <p className="mb-1">Add Amadeus credentials to enable live flight status:</p>
            <code className="text-xs text-[#8C8C8C]">AMADEUS_CLIENT_ID, AMADEUS_CLIENT_SECRET</code>
          </div>
        )}
      </div>
    </IntegrationCard>
  );
}

// ==================== MAIN SETTINGS PAGE ====================

export function SettingsPage() {
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchStatus();
  }, []);
  
  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/system/status`);
      setSystemStatus(res.data);
    } catch (err) {
      toast.error('Failed to load system status');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8A85A]" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#F5F1EB]">
      {/* Header */}
      <div className="bg-white border-b border-[rgba(0,0,0,0.06)]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl text-[#1A1A1A]">Settings</h1>
              <p className="text-sm text-[#8C8C8C]">System integrations and configuration</p>
            </div>
            <Button variant="ghost" onClick={fetchStatus}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* System Info */}
        <div className="mb-8 p-4 bg-white rounded-lg border border-[rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-[#C8A85A]" />
              <div>
                <p className="font-medium text-[#1A1A1A]">{systemStatus?.system}</p>
                <p className="text-xs text-[#8C8C8C]">Version {systemStatus?.version}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {systemStatus?.services && Object.entries(systemStatus.services).map(([name, status]) => (
                <span key={name} className="text-xs px-2 py-1 bg-[#8DA399]/10 text-[#8DA399] rounded">
                  {name.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        {/* Integrations */}
        <div className="space-y-6">
          <h2 className="font-serif text-lg text-[#1A1A1A]">Integrations</h2>
          
          <div className="grid gap-6">
            <CalendarSettings 
              status={systemStatus?.integrations?.calendar} 
              onRefresh={fetchStatus}
            />
            <EmailSettings status={systemStatus?.integrations?.email} />
            <FlightStatusSettings status={systemStatus?.integrations?.flight_status} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
